// NOTE: Provider class re-exports have been moved to index.ts (web) and
// index.native.ts (RN) to avoid eager resolution of web-only dependencies
// (react-dom, @privy-io/react-auth) when this module is imported on RN.
import { getConfig, WebSessionManager, ReactNativeSessionManager } from "@pooflabs/core";
// Web-only providers are lazily imported inside getAuthProvider() to avoid
// pulling react-dom / @privy-io/react-auth into the RN bundle.
import { MockAuthProvider } from "./providers/mock-auth-provider";
import { OffchainAuthProvider } from "./providers/offchain-auth-provider";
import { setAuthProviderInstance } from "../global";
import { getPlatform } from "../platform";
let currentAuthProvider = null;
let currentAuthMethod = null;
let initConfig = null;
// The effective Phantom provider (offchain-wrapped when chain:"offchain"), captured
// when a Phantom-primary app initializes. Used to restore Phantom after a staged
// Privy email swap is cancelled/fails, and to route method-specific login() calls
// back to Phantom. Null for non-Phantom apps.
let savedPhantomProvider = null;
/**
 * Restore the saved Phantom provider as the active auth provider. Used to undo a
 * staged Privy email swap on cancel/failure and to re-anchor method-specific
 * login() calls on Phantom. No-op when there is no saved Phantom provider.
 */
async function restoreSavedPhantomProvider() {
    if (!savedPhantomProvider)
        return;
    currentAuthProvider = savedPhantomProvider;
    currentAuthMethod = 'phantom';
    const coreConfig = await getConfig();
    coreConfig.authProvider = savedPhantomProvider;
    coreConfig.authMethod = 'phantom';
    setAuthProviderInstance(savedPhantomProvider);
}
// --- localStorage helpers: track which auth method the user last logged in with ---
const STORED_AUTH_METHOD_KEY = 'tarobase_last_auth_method';
function getStoredAuthMethod() {
    try {
        return getPlatform().storage.getItem(STORED_AUTH_METHOD_KEY);
    }
    catch (_a) {
        return null;
    }
}
function setStoredAuthMethod(method) {
    try {
        if (method) {
            getPlatform().storage.setItem(STORED_AUTH_METHOD_KEY, method);
        }
        else {
            getPlatform().storage.removeItem(STORED_AUTH_METHOD_KEY);
        }
    }
    catch (_a) {
        // storage might not be available
    }
}
/**
 * Clears the session if it was created before auth method tracking (legacy session).
 * Returns true if the session was cleared, false if the session is compatible.
 *
 * Dispatches to the correct session manager based on the current auth method
 * so both web (WebSessionManager) and RN (ReactNativeSessionManager) sessions
 * are properly cleared.
 */
export function clearIncompatibleSession() {
    if (getStoredAuthMethod() !== null) {
        return false; // Auth method is tracked — session is compatible
    }
    // Legacy session (no stored auth method) — clear it so the user re-authenticates
    // with the correct provider.
    // Clear both managers — on web the RN manager is a no-op (not configured),
    // on RN the web manager is a no-op (no localStorage).
    WebSessionManager.clearSession();
    try {
        ReactNativeSessionManager.clearSession();
    }
    catch ( /* not configured on web */_a) { /* not configured on web */ }
    return true;
}
// Lazy loaders for web-only providers.
// Using dynamic import() ensures Metro (RN) never resolves these modules.
async function loadPrivyWalletProvider() {
    const mod = await import("./providers/privy-wallet-provider");
    return mod.PrivyWalletProvider;
}
async function loadPhantomWalletProvider() {
    const mod = await import("./providers/phantom-wallet-provider");
    return { PhantomWalletProvider: mod.PhantomWalletProvider };
}
async function loadSolanaMobileWalletProvider() {
    const mod = await import("./providers/solana-mobile-wallet-provider");
    return mod.SolanaMobileWalletProvider;
}
async function hotSwapToPrivyProvider(config) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const rpcUrl = (_a = config.rpcUrl) !== null && _a !== void 0 ? _a : null;
    const PrivyWalletProvider = await loadPrivyWalletProvider();
    // Build an email-only Privy config: hide wallet connectors, show only email login.
    // Must include embeddedWallets so Privy auto-creates Solana wallets for email login users.
    const fallbackPrivyConfig = {
        appId: (_c = (_b = config.privyConfig) === null || _b === void 0 ? void 0 : _b.appId) !== null && _c !== void 0 ? _c : 'cm4ve714908o27dk2yurpnfhd',
        config: Object.assign(Object.assign({}, (_d = config.privyConfig) === null || _d === void 0 ? void 0 : _d.config), { loginMethods: ['email'], embeddedWallets: {
                solana: {
                    createOnLogin: 'users-without-wallets'
                }
            }, appearance: Object.assign(Object.assign({}, (_f = (_e = config.privyConfig) === null || _e === void 0 ? void 0 : _e.config) === null || _f === void 0 ? void 0 : _f.appearance), { walletChainType: 'solana-only', showWalletLoginFirst: false, theme: 'dark', walletList: [] }) })
    };
    const privyProvider = new PrivyWalletProvider((_g = config.name) !== null && _g !== void 0 ? _g : null, (_h = config.logoUrl) !== null && _h !== void 0 ? _h : null, fallbackPrivyConfig, rpcUrl);
    let provider = privyProvider;
    if (config.chain === "offchain") {
        provider = new OffchainAuthProvider(privyProvider);
    }
    // STAGED swap: do NOT mutate the module-level auth globals here. The caller (the
    // Phantom modal's email button / the `method:'email'` bridge) calls `.login()` on
    // the returned provider separately. If the user cancels, committing up-front would
    // strand the globals on Privy and break reload/signing. Instead, return a provider
    // whose `login()` commits the globals to Privy ONLY on success and restores the
    // saved Phantom provider on cancel/failure.
    const commitPrivy = async () => {
        currentAuthProvider = provider;
        currentAuthMethod = 'privy';
        const coreConfig = await getConfig();
        coreConfig.authProvider = provider;
        coreConfig.authMethod = 'privy';
        setAuthProviderInstance(provider);
    };
    // Proxy intercepts only `login`; every other access delegates to `provider`. The
    // proxy is used solely for this one `.login()` call — on success the globals are
    // committed to the inner `provider` (not the proxy), so subsequent calls bypass it.
    return new Proxy(provider, {
        get(target, prop, receiver) {
            if (prop === 'login') {
                return async (...args) => {
                    try {
                        const user = await target.login(...args);
                        if (user) {
                            await commitPrivy();
                        }
                        else {
                            await restoreSavedPhantomProvider();
                        }
                        return user;
                    }
                    catch (err) {
                        await restoreSavedPhantomProvider();
                        throw err;
                    }
                };
            }
            return Reflect.get(target, prop, receiver);
        },
    });
}
async function hotSwapToMWAProvider(config) {
    var _a, _b;
    const rpcUrl = (_a = config.rpcUrl) !== null && _a !== void 0 ? _a : null;
    const SolanaMobileWalletProvider = await loadSolanaMobileWalletProvider();
    const mwaConfig = (_b = config.mobileWalletConfig) !== null && _b !== void 0 ? _b : {
        appIdentity: {
            name: config.name || undefined,
            uri: getPlatform().getLocationOrigin(),
        },
    };
    const mwaProvider = new SolanaMobileWalletProvider(rpcUrl, mwaConfig);
    let provider = mwaProvider;
    if (config.chain === "offchain") {
        provider = new OffchainAuthProvider(mwaProvider);
    }
    currentAuthProvider = provider;
    currentAuthMethod = 'mobile-wallet-adapter';
    const coreConfig = await getConfig();
    coreConfig.authProvider = provider;
    // Send 'phantom' as authMethod so the server routes to Solana verification.
    // TODO: change back to 'mobile-wallet-adapter' once the backend is deployed.
    coreConfig.authMethod = 'phantom';
    setAuthProviderInstance(provider);
    return provider;
}
export const SOLANA_DEVNET_RPC_URL = "https://idelle-8nxsep-fast-devnet.helius-rpc.com";
export const SOLANA_MAINNET_RPC_URL = "https://celestia-cegncv-fast-mainnet.helius-rpc.com";
export const SURFNET_RPC_URL = "https://surfpool.fly.dev";
export async function getAuthProvider(config) {
    var _a, _b, _c;
    if (currentAuthProvider) {
        return currentAuthProvider;
    }
    if (!config) {
        throw new Error("Config is required to initialize auth provider");
    }
    initConfig = config;
    // If mockAuth is enabled, use MockAuthProvider regardless of authMethod
    if (config.mockAuth) {
        console.log("[MockAuth] Mock authentication enabled");
        currentAuthProvider = new MockAuthProvider();
        return currentAuthProvider;
    }
    // If the user previously logged in with a specific method, use that instead
    const validAuthMethods = ['privy', 'privy-expo', 'phantom', 'wallet', 'rainbowkit', 'coinbase-smart-wallet', 'onboard', 'mobile-wallet-adapter', 'none'];
    const storedMethod = getStoredAuthMethod();
    const authMethod = (storedMethod && validAuthMethods.includes(storedMethod))
        ? storedMethod
        : (config.authMethod || "phantom");
    if (storedMethod && !validAuthMethods.includes(storedMethod)) {
        // Invalid stored value — clear it
        setStoredAuthMethod(null);
    }
    const rpcUrl = (_a = config.rpcUrl) !== null && _a !== void 0 ? _a : null;
    currentAuthMethod = authMethod;
    switch (authMethod) {
        case "wallet":
            console.warn("Wallet auth is not yet supported.");
            break;
        case "rainbowkit":
            console.warn("Rainbow Kit auth is not yet supported.");
            break;
        case "privy": {
            const PrivyWalletProvider = await loadPrivyWalletProvider();
            currentAuthProvider = new PrivyWalletProvider((_b = config.name) !== null && _b !== void 0 ? _b : null, (_c = config.logoUrl) !== null && _c !== void 0 ? _c : null, config.privyConfig, rpcUrl);
            break;
        }
        case "phantom": {
            const { PhantomWalletProvider } = await loadPhantomWalletProvider();
            currentAuthProvider = new PhantomWalletProvider(rpcUrl, config.phantomConfig);
            // MWA hot-swap is always available (custom modal shows MWA button on Android)
            currentAuthProvider.onSwitchToMWA =
                () => hotSwapToMWAProvider(config);
            // Privy email bridge: ALWAYS wired, independent of enablePrivyFallback, so
            // `login({ method: 'email' })` works even on a custom Phantom app (which
            // sets enablePrivyFallback:false). enablePrivyFallback now only controls
            // whether the DEFAULT modal renders its own email button (see
            // phantom-wallet-provider.ts) — not whether the bridge exists.
            currentAuthProvider.onSwitchToPrivy =
                () => hotSwapToPrivyProvider(config);
            break;
        }
        case "privy-expo":
            // React Native: consumer must create PrivyExpoProvider and pass it via
            // config.privyExpoProvider, since the provider needs Privy hooks bridged
            // from the RN component tree.
            if (!config.privyExpoProvider) {
                throw new Error('authMethod "privy-expo" requires a PrivyExpoProvider instance passed as config.privyExpoProvider. ' +
                    'Create the provider, call setPrivyMethods() from your React Native component tree, ' +
                    'then pass it to init().');
            }
            currentAuthProvider = config.privyExpoProvider;
            break;
        case "mobile-wallet-adapter": {
            const SolanaMobileWalletProvider = await loadSolanaMobileWalletProvider();
            currentAuthProvider = new SolanaMobileWalletProvider(rpcUrl, config.mobileWalletConfig);
            break;
        }
        case "onboard":
            console.warn("Onboard auth is not yet supported.");
            break;
        default:
            break;
    }
    if (!currentAuthProvider) {
        throw new Error("No auth provider was selected. Does your app have a supported auth method or chain configured?");
    }
    // Wrap with OffchainAuthProvider for offchain/poofnet chain
    if (config.chain === "offchain") {
        console.log("[Offchain] Wrapping auth provider for Poofnet transaction tracking");
        currentAuthProvider = new OffchainAuthProvider(currentAuthProvider);
    }
    // Save the EFFECTIVE Phantom provider (post offchain-wrap) so the staged Privy
    // email swap can restore it on cancel/failure and method-specific login() calls
    // can re-anchor on Phantom. Captured after the wrap so restore doesn't bypass the
    // OffchainAuthProvider on Poofnet/preview.
    if (authMethod === 'phantom') {
        savedPhantomProvider = currentAuthProvider;
    }
    return currentAuthProvider;
}
export async function login(options) {
    // Re-initialize provider if it was cleared by logout
    if (!currentAuthProvider && initConfig) {
        currentAuthProvider = await getAuthProvider(initConfig);
        const coreConfig = await getConfig();
        coreConfig.authProvider = currentAuthProvider;
        // Map client-only auth methods to backend-compatible values
        coreConfig.authMethod = (currentAuthMethod === 'privy-expo' ? 'privy' : currentAuthMethod);
        setAuthProviderInstance(currentAuthProvider);
    }
    if (!currentAuthProvider) {
        throw new Error("Auth provider not initialized. Please call init() first.");
    }
    // Method-specific login() always operates on the Phantom provider (which then
    // bridges to Privy for 'email'). If a prior email swap committed Privy, re-anchor
    // on the saved Phantom provider before delegating so the `method` directive lands
    // on Phantom. (Cancelled swaps already restore Phantom via the staged proxy; this
    // covers the post-success case.) No-op for non-Phantom apps (no saved provider).
    if ((options === null || options === void 0 ? void 0 : options.method) && currentAuthMethod !== 'phantom' && savedPhantomProvider) {
        await restoreSavedPhantomProvider();
    }
    // Forward per-call overrides to providers that support them (duck-typed to stay
    // lazy-load safe — no direct import of PhantomWalletProvider here).
    // Always call (even with null) so previous overrides are cleared when this
    // login() is invoked without options — early-return paths inside the provider
    // bypass the .finally() cleanup, so we reset here too.
    if (typeof currentAuthProvider.setLoginOverrides === 'function') {
        currentAuthProvider.setLoginOverrides(options !== null && options !== void 0 ? options : null);
    }
    const loginResult = await currentAuthProvider.login();
    if (loginResult) {
        // Store which auth method was used so we restore the right provider on reload
        setStoredAuthMethod(currentAuthMethod);
        // If this top-level tab was opened solely to perform the wallet connect for
        // an embedded preview (?poofConnect — see openTopLevelForConnect in the
        // phantom provider), close it now that the session is established. The
        // session reflects back into the preview's iframe via same-origin storage.
        try {
            if (typeof window !== 'undefined'
                && window.top === window.self
                && new URLSearchParams(window.location.search).get('poofConnect') === '1') {
                setTimeout(() => { try {
                    window.close();
                }
                catch ( /* noop */_a) { /* noop */ } }, 400);
            }
        }
        catch ( /* noop */_a) { /* noop */ }
        return Object.assign(Object.assign({}, loginResult), { provider: currentAuthProvider });
    }
    return null;
}
export function getCurrentAuthMethod() {
    // Fall back to the persisted login method so consumers (e.g. a "show export
    // wallet" gate) get the correct value synchronously on reload, before init()
    // has rebuilt the in-memory provider. The stored value is cleared on logout,
    // so a logged-out user still yields null.
    return currentAuthMethod !== null && currentAuthMethod !== void 0 ? currentAuthMethod : getStoredAuthMethod();
}
export async function logout() {
    if (!currentAuthProvider) {
        throw new Error("Auth provider not initialized. Please call init() first.");
    }
    await currentAuthProvider.logout();
    setStoredAuthMethod(null);
    currentAuthProvider = null;
    currentAuthMethod = null;
}
/**
 * Opens the active provider's wallet-export flow (Privy's hosted modal). Only
 * embedded (email) wallets implement `exportWallet`; external wallets (Phantom)
 * throw. The private key is revealed only to the user on Privy's origin — it is
 * never exposed to this SDK or the host app.
 */
export async function exportWallet(address) {
    if (!currentAuthProvider) {
        throw new Error("Auth provider not initialized. Please call init() first.");
    }
    if (typeof currentAuthProvider.exportWallet !== 'function') {
        throw new Error("Wallet export is only available for embedded (email) wallets.");
    }
    await currentAuthProvider.exportWallet(address);
}
